// Epoch 19: Lead Generator Enhancements - Enhanced Lead Preview Component
// File: src/components/filters/EnhancedLeadPreview.tsx
// Purpose: Enhanced preview with lead count, cost display, tier distribution, and privacy protection

import React, { useState, useEffect } from 'react';
import { MultiSelectFilterState, FilterMetadata } from '../../types/filters';
import { supabase } from '../../supabase/client';

interface EnhancedLeadPreviewProps {
  filters: MultiSelectFilterState;
  metadata: FilterMetadata | null;
  onExportRequest: (leadCount: number, estimatedCost: number) => void;
  className?: string;
}

interface PreviewLead {
  h_Lead_Score: number;
  norm_country: string;
  norm_city: string;
  norm_job_level: string;
  norm_job_function: string;
  norm_industry: string;
  h_Company: string;
  h_Position: string;
  h_Location: string;
  tier: string;
}

interface PreviewData {
  totalCount: number;
  sampleLeads: PreviewLead[];
  tierDistribution: Record<string, number>;
  filterSummary: {
    countries: number;
    cities: number;
    jobLevels: number;
    jobFunctions: number;
    industries: number;
    tiers: number;
    hasActiveFilters: boolean;
  };
}

// Pricing constants (should match backend)
const BASE_PRICE = 50; // $50 per 1000 leads
const TIER_PRICING = {
  '1': 0.10,
  '2': 0.08,
  '3': 0.05,
  '4': 0.03,
  '5': 0.01,
};

export default function EnhancedLeadPreview({
  filters,
  onExportRequest,
  className = ''
}: Omit<EnhancedLeadPreviewProps, 'metadata'>) {
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if filters have meaningful selections
  const hasActiveFilters = filters.countries.length > 0 || 
                          filters.cities.length > 0 || 
                          filters.jobLevels.length > 0 || 
                          filters.jobFunctions.length > 0 || 
                          filters.industries.length > 0 ||
                          (filters.tiers.length > 0 && filters.tiers.length < 5);

  // Fetch preview data when filters change
  useEffect(() => {
    if (!hasActiveFilters) {
      setPreviewData(null);
      return;
    }

    const fetchPreviewData = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('Authentication required');
        }

        const response = await fetch(
          'https://zeeovxbtdyxncikfonij.supabase.co/functions/v1/getFilteredLeads',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify(filters)
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch preview: ${response.status}`);
        }

        const data: PreviewData = await response.json();
        setPreviewData(data);
      } catch (err) {
        console.error('Preview fetch error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load preview');
      } finally {
        setLoading(false);
      }
    };

    fetchPreviewData();
  }, [filters, hasActiveFilters]);

  // Calculate estimated cost
  const calculateEstimatedCost = (): number => {
    if (!previewData) return 0;

    const baseCost = BASE_PRICE * (previewData.totalCount / 1000);
    let tierUpcharge = 0;

    Object.entries(previewData.tierDistribution).forEach(([tier, count]) => {
      tierUpcharge += count * (TIER_PRICING[tier as keyof typeof TIER_PRICING] || 0);
    });

    return baseCost + tierUpcharge;
  };

  const estimatedCost = calculateEstimatedCost();

  // Handle export request
  const handleExportRequest = () => {
    if (previewData) {
      onExportRequest(previewData.totalCount, estimatedCost);
    }
  };

  // Render no filters state
  if (!hasActiveFilters) {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Filters Applied</h3>
          <p className="text-gray-600 text-sm">
            Select filters from the sidebar to preview matching leads
          </p>
        </div>
      </div>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading preview...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={`bg-white border border-red-200 rounded-lg p-6 ${className}`}>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-red-900 mb-2">Preview Error</h3>
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // Render no results state
  if (previewData && previewData.totalCount === 0) {
    return (
      <div className={`bg-white border border-orange-200 rounded-lg p-6 ${className}`}>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-orange-900 mb-2">No Leads Found</h3>
          <p className="text-orange-600 text-sm">
            Try adjusting your filters to find matching leads
          </p>
        </div>
      </div>
    );
  }

  // Render preview results
  return (
    <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      {/* Preview Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Preview Results ({previewData?.totalCount.toLocaleString()} leads found)
          </h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Estimated cost:</span>
            <span className="text-lg font-bold text-green-600">
              ${estimatedCost.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Tier Distribution */}
        {previewData && Object.keys(previewData.tierDistribution).length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Lead Quality Distribution</h4>
            <div className="flex space-x-2">
              {Object.entries(previewData.tierDistribution)
                .filter(([, count]) => count > 0)
                .map(([tier, count]) => (
                  <div key={tier} className="bg-white rounded-lg px-3 py-2 shadow-sm border">
                    <div className="text-xs text-gray-600">Tier {tier}</div>
                    <div className="text-sm font-semibold">{count.toLocaleString()}</div>
                    <div className="text-xs text-green-600">
                      +${TIER_PRICING[tier as keyof typeof TIER_PRICING]?.toFixed(2)}/lead
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Export Button */}
        <button
          onClick={handleExportRequest}
          className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>Export {previewData?.totalCount.toLocaleString()} Leads to CSV - ${estimatedCost.toFixed(2)}</span>
        </button>
      </div>

      {/* Sample Lead Data */}
      {previewData && previewData.sampleLeads.length > 0 && (
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-medium text-gray-700">Sample Lead Data (Privacy Protected)</h4>
            <span className="text-xs text-gray-500">
              Showing {previewData.sampleLeads.length} of {previewData.totalCount.toLocaleString()} leads
            </span>
          </div>

          {/* Privacy Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-start space-x-2">
              <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <p className="text-sm text-yellow-800 font-medium">Privacy Protection Active</p>
                <p className="text-xs text-yellow-700 mt-1">
                  Contact information (names, emails, phones, LinkedIn URLs) is hidden in preview. 
                  Full data available after export.
                </p>
              </div>
            </div>
          </div>

          {/* Sample Leads Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Industry</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {previewData.sampleLeads.slice(0, 10).map((lead, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-900">{lead.h_Company || 'N/A'}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{lead.h_Position || 'N/A'}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{lead.h_Location || lead.norm_city || 'N/A'}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{lead.norm_industry || 'N/A'}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{lead.h_Lead_Score}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        lead.tier === '1' ? 'bg-green-100 text-green-800' :
                        lead.tier === '2' ? 'bg-blue-100 text-blue-800' :
                        lead.tier === '3' ? 'bg-yellow-100 text-yellow-800' :
                        lead.tier === '4' ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        Tier {lead.tier}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Show More Leads Notice */}
          {previewData.totalCount > 10 && (
            <div className="text-center mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                {(previewData.totalCount - 10).toLocaleString()} more leads available in full export
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}